import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { useAddLink } from "@/api/hooks/files";
import { errorMessage, isApiError } from "@/api/errors";
import { Field, FormError } from "@/components/Field";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const schema = z.object({
  title: z.string().trim().min(1, "files.titleRequired"),
  url: z
    .string()
    .trim()
    .refine((v) => /^https:\/\/[^\s/$.?#].[^\s]*$/i.test(v), "files.httpsOnly"),
  description: z.string().trim(),
});
type Values = z.infer<typeof schema>;

export function AddLinkDialog({ org, project, open, onOpenChange }: { org: string; project: string; open: boolean; onOpenChange: (o: boolean) => void }) {
  const { t } = useTranslation();
  const add = useAddLink(org, project);
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { title: "", url: "", description: "" } });
  const { errors } = form.formState;
  const serverUrlError = isApiError(add.error) ? add.error.errors.url?.[0] : undefined;

  const onSubmit = form.handleSubmit((v) =>
    add.mutate(
      { title: v.title, url: v.url, ...(v.description ? { description: v.description } : {}) },
      {
        onSuccess: () => {
          toast.success(t("files.linkAdded"));
          form.reset();
          onOpenChange(false);
        },
      },
    ),
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          form.reset();
          add.reset();
        }
        onOpenChange(o);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("files.addLinkTitle")}</DialogTitle>
          <DialogDescription>{t("files.addLinkIntro")}</DialogDescription>
        </DialogHeader>
        <form noValidate onSubmit={onSubmit} className="flex flex-col gap-4">
          <FormError message={add.isError && !serverUrlError ? errorMessage(add.error, t("errors.generic")) : null} />
          <Field label={t("files.titleLabel")} error={errors.title?.message ? t(errors.title.message) : null}>
            {(p) => <Input {...p} maxLength={200} {...form.register("title")} />}
          </Field>
          <Field
            label={t("files.urlLabel")}
            hint={t("files.urlHint")}
            error={errors.url?.message ? t(errors.url.message) : (serverUrlError ?? null)}
          >
            {(p) => <Input {...p} type="url" inputMode="url" placeholder="https://" {...form.register("url")} />}
          </Field>
          <Field label={t("files.descriptionLabel")}>
            {(p) => <Textarea {...p} rows={3} {...form.register("description")} />}
          </Field>
          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={add.isPending}>
              {t("files.saveLink")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
